import { Box } from "@mui/material";
import type { FC } from "react";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { AgencyRuleSection } from "./AgencyRuleSection";
import { DepartmentRuleSections } from "./DepartmentRuleSections";
import { type RuleCardSubject, type RuleSubject, subjectDepartmentId } from "./ruleCardSubjects";

type Props = {
	subjects: readonly RuleCardSubject[];
	/** The card's name in test ids: what it hangs from. */
	dataTestId: string;
};

/**
 * DAT-906 · The body of a wage breakdown hover card: the configuration rows behind a name, as today's
 * settings pages show them — this department's rules first, then any rule the employee's home department
 * holds, then the agency's rates. Only mounted while the card is open, which is what makes it load lazily.
 */
export const RuleCard: FC<Props> = ({ subjects, dataTestId }) => {
	const { chosenDepartment } = useHospitalData();
	const viewedDepartmentId = chosenDepartment.department_id;
	const rules = subjects.flatMap((subject): RuleSubject[] => (subject.kind === "agency" ? [] : [subject]));
	// This department first; a Set keeps first-seen order.
	const departmentIds = [...new Set([viewedDepartmentId, ...rules.map((rule) => subjectDepartmentId(rule, viewedDepartmentId))])];
	return (
		<Box data-testid={dataTestId} sx={{ display: "flex", flexDirection: "column", gap: 0.5, pb: 1 }}>
			{departmentIds.map((departmentId) => {
				const departmentRules = rules.filter((rule) => subjectDepartmentId(rule, viewedDepartmentId) === departmentId);
				if (departmentRules.length === 0) return null;
				return (
					<DepartmentRuleSections
						key={departmentId}
						departmentId={departmentId}
						isHomeDepartment={departmentId !== viewedDepartmentId}
						subjects={departmentRules}
					/>
				);
			})}
			{subjects.some((subject) => subject.kind === "agency") && <AgencyRuleSection />}
		</Box>
	);
};
